import client from "../../client/client";

class AiAgentsService {
	deleteDefinition({ agentId }, cancelToken) {
		return client.delete(`/ai/agents/definition/${agentId}`, {
			config: { cancelToken },
		});
	}

	updateAgentDefinition(
		{ agentId },
		{ title, prompt, creatorId, pattern, streamingSize, streamingType, tokenLimit, toolLimit, modelId },
		cancelToken
	) {
		return client.patch(`/ai/agents/${agentId}/update-definition`, {
			payload: {
				title,
				prompt,
				creatorId,
				pattern,
				streamingSize,
				streamingType,
				tokenLimit,
				toolLimit,
				modelId,
			},
			config: { cancelToken },
		});
	}

	createAgentDefinition(
		{
			title = "",
			prompt = "",
			creatorId = null,
			pattern = null,
			streamingSize = null,
			streamingType = null,
			tokenLimit = undefined,
			toolLimit = undefined,
			modelId = undefined,
		},
		cancelToken
	) {
		return client.put("/ai/agents/create-definition", {
			payload: {
				title,
				prompt,
				creatorId,
				pattern,
				streamingSize,
				streamingType,
				tokenLimit,
				toolLimit,
				modelId,
			},
			config: { cancelToken },
		});
	}

	downloadTrace({ executionId }, cancelToken) {
		return client.download(`/ai/agents/${executionId}/download-trace`, {
			config: { cancelToken },
		});
	}

	executeAgent({ agentId, projectId, companyId, parameters }, cancelToken) {
		return client.post("/ai/agents/execute-agent", {
			payload: { agentId, projectId, companyId, parameters },
			config: { cancelToken },
		});
	}

	deleteExecution({ executionId }, cancelToken) {
		return client.delete(`/ai/agents/execution/${executionId}`, {
			config: { cancelToken },
		});
	}

	getDefinitionList(
		{ definitionId, userId, titleContains, promptContains, createdAfter, pattern, page, size, sort },
		cancelToken
	) {
		return client.get("/ai/agents/list-definitions", {
			params: { definitionId, userId, titleContains, promptContains, createdAfter, pattern, page, size, sort },
			config: { cancelToken },
		});
	}

	getExecutionList({ agentName = "", page = 0, size = 25, sort = "" }, cancelToken) {
		return client.get("/ai/agents/list-execution", {
			params: { agentName, page, size, sort },
			config: { cancelToken },
		});
	}

	getAgentList({ page = 0, size = 25, direction = "id;DESC", ...options }, cancelToken) {
		return client.get("/ai/agents/filter-agents", {
			params: { page, size, direction, ...options },
			config: { cancelToken },
		});
	}

	getToolList(cancelToken) {
		return client.get("/ai/agents/list-tools", {
			config: { cancelToken },
		});
	}

	getModelList({ nameContains, providerType, active, page, size, sort }, cancelToken) {
		return client.get("/ai/agent-models/list", {
			params: { nameContains, providerType, active, page, size, sort },
			config: { cancelToken },
		});
	}

	createModel(
		{
			name,
			description,
			providerType,
			baseUrl,
			deploymentName,
			modelName,
			apiKey,
			region,
			projectId,
			location,
			accessKey,
			secretKey,
			providerOptions,
			active,
			inputCostPerMillion,
			outputCostPerMillion,
		},
		cancelToken
	) {
		return client.put("/ai/agent-models/create", {
			payload: {
				name,
				description,
				providerType,
				baseUrl,
				deploymentName,
				modelName,
				apiKey,
				region,
				projectId,
				location,
				accessKey,
				secretKey,
				providerOptions,
				active,
				inputCostPerMillion,
				outputCostPerMillion,
			},
			config: { cancelToken },
		});
	}

	updateModel(
		{ modelId },
		{
			name,
			description,
			providerType,
			baseUrl,
			deploymentName,
			modelName,
			apiKey,
			region,
			projectId,
			location,
			accessKey,
			secretKey,
			providerOptions,
			active,
			inputCostPerMillion,
			outputCostPerMillion,
		},
		cancelToken
	) {
		return client.post(`/ai/agent-models/${modelId}`, {
			payload: {
				name,
				description,
				providerType,
				baseUrl,
				deploymentName,
				modelName,
				apiKey,
				region,
				projectId,
				location,
				accessKey,
				secretKey,
				providerOptions,
				active,
				inputCostPerMillion,
				outputCostPerMillion,
			},
			config: { cancelToken },
		});
	}

	deleteModel({ modelId }, cancelToken) {
		return client.delete(`/ai/agent-models/${modelId}`, {
			config: { cancelToken },
		});
	}

	exportAllModels(cancelToken) {
		return client.download("/ai/agent-models/export", {
			config: { cancelToken },
		});
	}

	exportModelById({ modelId }, cancelToken) {
		return client.download(`/ai/agent-models/export/${modelId}`, {
			config: { cancelToken },
		});
	}

	importModels(payload, cancelToken) {
		return client.post("/ai/agent-models/import", {
			payload,
			config: { cancelToken },
		});
	}

	getSpawnableAgents({ parentAgentId }, cancelToken) {
		return client.get(`/ai/agents/${parentAgentId}/spawnable-agents`, {
			config: { cancelToken },
		});
	}

	linkSpawnableAgent({ parentAgentId, spawnedAgentId }, cancelToken) {
		return client.put(`/ai/agents/${parentAgentId}/spawnable-agents/${spawnedAgentId}`, {
			config: { cancelToken },
		});
	}

	unlinkSpawnableAgent({ parentAgentId, spawnedAgentId }, cancelToken) {
		return client.delete(`/ai/agents/${parentAgentId}/spawnable-agents/${spawnedAgentId}`, {
			config: { cancelToken },
		});
	}

	agentRag(payload, cancelToken) {
		return client.post("/ai/agents/rag", {
			payload,
			config: { cancelToken },
		});
	}

	getProposedFieldValues({ projectId, fieldIds }, cancelToken) {
		return client.get("/ai/proposed-field-values/by-project-fields", {
			payload: { projectId, fieldIds },
			config: { cancelToken },
		});
	}

	updateProposedFieldValues({ proposalId, newStatus }, cancelToken) {
		return client.get("/ai/proposed-field-values/by-project-fields", {
			payload: { proposalId, newStatus },
			config: { cancelToken },
		});
	}

	getAgentTools({ agentId, filter }, cancelToken) {
		return client.get(`/api/agents/${agentId}/tools`, {
			params: { agentId, filter },
			config: { cancelToken },
		});
	}

	updateAgentTools({ agentId }, { toolIds }, cancelToken) {
		return client.put(`/api/agents/${agentId}/tools`, {
			params: { agentId },
			payload: { toolIds },
			config: { cancelToken },
		});
	}

	exportAgents(cancelToken) {
		return client.get("/ai/agents/export", {
			config: { cancelToken },
		});
	}

	exportAgent({ agentId }, cancelToken) {
		return client.download(`/ai/agents/export/${agentId}`, {
			config: { cancelToken },
		});
	}

	importAgents(payload, cancelToken) {
		return client.post("/ai/agents/import", {
			payload,
			config: { cancelToken },
		});
	}

	duplicateAgent({ id: agentId }, cancelToken) {
		return client.post(`/ai/agents/${agentId}/duplicate`, {
			params: { agentId },
			config: { cancelToken },
		});
	}

	getExecutedAgent({ executionId }, cancelToken) {
		return client.get(`/ai/agents/list-execution/${executionId}`, {
			params: { executionId },
			config: { cancelToken },
		});
	}

	getKillSwitchStatus(cancelToken) {
		return client.get("/ai/agents/killswitch", {
			config: { cancelToken },
		});
	}

	updateKillSwitchStatus({ value }, cancelToken) {
		return client.post("/ai/agents/killswitch", {
			payload: { value },
			config: { cancelToken },
		});
	}
}
export default new AiAgentsService();
